import type { JSX } from 'react';
import type { CompanyListItemResource, CompanyStatusValue } from '@voxler/contracts';
import { Link, useNavigate } from '@tanstack/react-router';
import { Ellipsis, Plus } from 'lucide-react';
import { useState } from 'react';
import { CreateCompanyDialog } from '../components/crm/CompanyDialog';
import { CreateDealDialog } from '../components/crm/DealDialog';
import { ReminderDialog, type ReminderDialogMode } from '../components/crm/ReminderDialog';
import { useCompanies } from '../components/crm/shared';
import { PageChrome } from '../components/page-chrome';
import {
  COMPANY_STATUS_BADGE,
  COMPANY_STATUS_LABEL,
  eurOrDash,
  formatInstantDate,
  formatNextAction,
} from '../lib/crm';
import { formatEurCents, initials } from '../lib/format';
import { Callout } from '@/components/ui/alert';
import { CellUser } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBar } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty';
import { SearchInput } from '@/components/ui/field';
import { Row4 } from '@/components/ui/layout-grid';
import { PageHead } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import { StatCard } from '@/components/ui/stat-card';
import {
  Cell2,
  rowLinkClass,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';

type CompanyTab = 'all' | CompanyStatusValue;

const COLUMN_COUNT = 9;

function NextActionCell({ company }: { company: CompanyListItemResource }): JSX.Element {
  if (company.flag === 'late') {
    return <Badge variant="warning">Rappel en retard</Badge>;
  }
  if (company.flag === 'no_next_action') {
    return <Badge variant="warning">Sans prochaine action</Badge>;
  }
  if (company.nextAction === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <Cell2
      primary={<span className="font-mono">{formatNextAction(company.nextAction)}</span>}
      secondary={company.nextAction.subject}
    />
  );
}

export function CompaniesPage(): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<CompanyTab>('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [dealFor, setDealFor] = useState<string | null>(null);
  const [reminder, setReminder] = useState<ReminderDialogMode | null>(null);

  const companies = useCompanies();
  const all = companies.data?.companies ?? [];
  const stats = companies.data?.stats;
  const loaded = companies.data !== undefined;
  const count = (status: CompanyTab): number | undefined =>
    loaded
      ? all.filter((company) => status === 'all' || company.status === status).length
      : undefined;

  const query = search.trim().toLowerCase();
  const digits = query.replace(/\s/g, '');
  const rows = all
    .filter((company) => tab === 'all' || company.status === tab)
    .filter(
      (company) =>
        query === '' ||
        `${company.name} ${company.primaryContact?.name ?? ''}`.toLowerCase().includes(query) ||
        (digits !== '' && (company.siren ?? '').includes(digits)),
    );
  const pagination = usePagination(rows, 25);

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Entreprises' }]}
        actions={
          <Button
            type="button"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus aria-hidden="true" />
            Nouvelle entreprise
          </Button>
        }
      />
      <PageHead
        title="Entreprises"
        subtitle="Prospects et clients. Un client est une entreprise rattachée à au moins un compte facturé. Montants HT."
      />

      {companies.isError ? (
        <Callout variant="destructive">Impossible de charger les entreprises.</Callout>
      ) : null}

      <Row4 aria-label="Indicateurs entreprises">
        <StatCard
          label="Prospects"
          value={stats === undefined ? '…' : String(stats.prospects)}
          foot={
            stats === undefined
              ? undefined
              : `dont ${stats.prospectsWithOpenDeal} avec une affaire ouverte`
          }
        />
        <StatCard
          label="Clients"
          value={stats === undefined ? '…' : String(stats.clients)}
          foot={
            stats === undefined
              ? undefined
              : `${stats.linkedAccounts} compte${stats.linkedAccounts > 1 ? 's' : ''} facturé${stats.linkedAccounts > 1 ? 's' : ''} rattaché${stats.linkedAccounts > 1 ? 's' : ''}`
          }
        />
        <StatCard
          label="Abonnements signés"
          value={stats === undefined ? '…' : formatEurCents(stats.signedMonthly)}
          foot="par mois, HT, tous clients"
        />
      </Row4>

      <Card>
        <CardBar
          start={
            <TableTabs<CompanyTab>
              aria-label="Filtrer les entreprises"
              value={tab}
              onValueChange={setTab}
              items={[
                { value: 'all', label: 'Toutes', count: count('all') },
                { value: 'prospect', label: 'Prospects', count: count('prospect') },
                { value: 'client', label: 'Clients', count: count('client') },
                { value: 'former_client', label: 'Anciens clients', count: count('former_client') },
              ]}
            />
          }
          end={
            <SearchInput
              label="Rechercher une entreprise"
              placeholder="Rechercher une entreprise, un contact ou un SIREN"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          }
        />
        {loaded && rows.length === 0 && tab === 'former_client' && query === '' ? (
          <EmptyState
            title="Aucun ancien client"
            description="Une entreprise passe ici quand son dernier compte facturé est résilié."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead kind="name">Entreprise</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Contact principal</TableHead>
                <TableHead kind="num">Affaires ouvertes</TableHead>
                <TableHead kind="num">Pondéré</TableHead>
                <TableHead kind="num">Abonnement / mois</TableHead>
                <TableHead>Dernière activité</TableHead>
                <TableHead>Prochaine action</TableHead>
                <TableHead kind="act">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagination.pageItems.map((company) => (
                <TableRow
                  key={company.id}
                  onClick={() =>
                    void navigate({
                      to: '/companies/$companyId',
                      params: { companyId: company.id },
                    })
                  }
                >
                  <TableCell kind="name">
                    <CellUser initials={initials(company.name)}>
                      <Cell2
                        primary={
                          <Link
                            to="/companies/$companyId"
                            params={{ companyId: company.id }}
                            className={rowLinkClass}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {company.name}
                          </Link>
                        }
                        secondary={
                          [company.address, company.sector]
                            .filter((part): part is string => part !== null && part !== '')
                            .join(', ') || undefined
                        }
                      />
                    </CellUser>
                  </TableCell>
                  <TableCell>
                    <Badge variant={COMPANY_STATUS_BADGE[company.status]}>
                      {COMPANY_STATUS_LABEL[company.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {company.primaryContact !== null ? (
                      <Cell2
                        primary={company.primaryContact.name}
                        secondary={company.primaryContact.jobTitle ?? undefined}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell kind="num">{company.openDeals}</TableCell>
                  <TableCell kind="num">{eurOrDash(company.weighted)}</TableCell>
                  <TableCell kind="num">{eurOrDash(company.signedMonthly)}</TableCell>
                  <TableCell kind="mono">
                    {company.lastActivityAt !== null
                      ? formatInstantDate(company.lastActivityAt)
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <NextActionCell company={company} />
                  </TableCell>
                  <TableCell
                    kind="act"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Actions pour ${company.name}`}
                        >
                          <Ellipsis aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => {
                            void navigate({
                              to: '/companies/$companyId',
                              params: { companyId: company.id },
                            });
                          }}
                        >
                          Ouvrir la fiche
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            setDealFor(company.id);
                          }}
                        >
                          Nouvelle affaire
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            setReminder({ kind: 'create', companyId: company.id });
                          }}
                        >
                          Programmer un rappel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {companies.isLoading ? (
                <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
              ) : null}
              {loaded && rows.length === 0 ? (
                <TableEmptyRow colSpan={COLUMN_COUNT}>
                  Aucune entreprise ne correspond.
                </TableEmptyRow>
              ) : null}
            </TableBody>
          </Table>
        )}
        <Pager itemLabel="entreprises" {...pagination.pagerProps} />
      </Card>

      <CreateCompanyDialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
      />
      <CreateDealDialog
        open={dealFor !== null}
        companyId={dealFor}
        onClose={() => {
          setDealFor(null);
        }}
      />
      <ReminderDialog
        mode={reminder}
        onClose={() => {
          setReminder(null);
        }}
      />
    </>
  );
}
